import { computed } from 'mobx';
import { IHarmonization } from '../../main/entities';
import { mg } from '../../mg/mg';
import { DelegateRepo } from './DelegateRepo';

class Multiset<E> {
	private readonly map = new Map<E, number>();

	count = (element: E) => this.map.get(element) ?? 0;
	add = (element: E) => {
		this.map.set(element, this.count(element) + 1);
	}
}

export class HarmonizationRepo extends DelegateRepo<IHarmonization> {
	@computed private get primary() : Array<IHarmonization> {
		return this.delegate.allAsList.filter(h => h.state === 'PRIMARY');
	}

	@computed private get secondary() : Array<IHarmonization> {
		return this.delegate.allAsList.filter(h => h.state === 'SECONDARY');
	}

	@computed get primaryCountsByItemId() : Multiset<number> {
		const r = new Multiset<number>();
		this.primary.forEach(h => {
			mg.assertPresent(h.masterItemId);
			r.add(h.masterItemId);
			r.add(h.id);
		});
		return r;
	}

	@computed private get secondaryCountsByItemId() : Multiset<number> {
		const r = new Multiset<number>();
		this.secondary.forEach(h => {
			mg.assertPresent(h.masterItemId);
			r.add(h.masterItemId);
			r.add(h.id);
		});
		return r;
	}

	@computed private get lastHarmonizationByMasterItemIdMap() : Map<number, IHarmonization> {
		const r = new Map<number, IHarmonization>();
		this.delegate.allAsList.forEach(h => {
			mg.assertPresent(h.masterItemId);
			const old = r.get(h.masterItemId);
			if (!old || old.timestamp < h.timestamp) r.set(h.masterItemId, h);
		});
		return r;
	}

	lastHarmonizationByMasterItemId(masterItemId: number) : IHarmonization|undefined {
		return this.lastHarmonizationByMasterItemIdMap.get(masterItemId);
	}

	hasAnyHarmonizationByItemId(itemId: number) {
		return this.primaryCountsByItemId.count(itemId) > 0 || this.secondaryCountsByItemId.count(itemId) > 0;
	}

	@computed private get countsBySupplierId() : Multiset<number> {
		const r = new Multiset<number>();
		this.primary.forEach(h => r.add(h.supplierId));
		return r;
	}

	countBySupplierId(supplierId: number) : number {
		return this.countsBySupplierId.count(supplierId);
	}

	@computed private get countByMasterItemAndSupplierMap() : Multiset<string> {
		const r = new Multiset<string>();
		this.primary.forEach(h => {
			if (!h.masterItemId) return;
			r.add(h.masterItemId + ' ' + h.supplierId);
		});
		return r;
	}

	countByMasterItemAndSupplier(masterItemId: number, supplierId: number) {
		return this.countByMasterItemAndSupplierMap.count(masterItemId + ' ' + supplierId) || 0;
	}
}
